import React, { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Search, MapPin, LocateFixed, Loader2 } from 'lucide-react';
import { geoSearch } from '@/lib/api';
import { useCity } from '@/context/CityContext';
import { toast } from 'sonner';

export default function CitySearchSheet({ open, onOpenChange }) {
  const { setCity, detectAndSet, detecting } = useCity();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); return; }
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await geoSearch(q);
        setResults(r || []);
      } catch (e) {
        toast.error('Search failed');
      } finally { setLoading(false); }
    }, 320);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const pick = (city) => {
    setCity(city);
    onOpenChange(false);
    toast.success(`Switched to ${city.name}`);
  };

  const useMyLocation = async () => {
    try {
      const c = await detectAndSet();
      onOpenChange(false);
      toast.success(`Located: ${c.name}`);
    } catch (e) {
      toast.error('Could not detect location');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-[28px] p-0 h-[78dvh] bg-white">
        <SheetHeader className="px-5 pt-5 pb-3 text-left">
          <SheetTitle className="text-xl">Choose a city</SheetTitle>
        </SheetHeader>
        <div className="px-5 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search any city..."
              className="pl-9 h-12 rounded-2xl bg-[#0F1A13]/05 border-black/10"
              data-testid="city-search-input"
            />
          </div>
          <button
            type="button"
            onClick={useMyLocation}
            disabled={detecting}
            className="mt-3 puvio-pill puvio-pill-accent w-full justify-center h-11 puvio-focus"
            data-testid="city-search-use-location"
          >
            {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
            <span>{detecting ? 'Locating...' : 'Use my location'}</span>
          </button>
        </div>
        <div className="px-2 overflow-y-auto h-[calc(78dvh-200px)]">
          {loading && (
            <div className="p-4 text-sm text-black/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Searching...</div>
          )}
          {!loading && q.length >= 2 && results.length === 0 && (
            <div className="p-4 text-sm text-black/50">No matches. Try a different query.</div>
          )}
          <ul className="flex flex-col">
            {results.map((r, i) => (
              <li key={`${r.lat}-${r.lon}-${i}`}>
                <button
                  type="button"
                  onClick={() => pick(r)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#0F1A13]/05 rounded-xl text-left puvio-focus"
                  data-testid={`city-search-result-${i}`}
                >
                  <span className="w-9 h-9 rounded-full bg-[#C4FF3D]/30 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-[#15291C]" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium truncate">{r.name}{r.country ? `, ${r.country}` : ''}</span>
                    <span className="block text-xs text-black/50 truncate">{r.label}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </SheetContent>
    </Sheet>
  );
}
